import React, { useEffect, useState } from 'react';

import { Button, Popover, TextFieldProps } from '@mui/material';
import { useDebounce } from 'hooks/useDebounce';
import { DateObject } from 'react-multi-date-picker';
import { Calendar } from 'shared/mui/Calendar/Calendar';
import { DatePickerActions } from 'shared/mui/DatePicker/components/DatePickerActions/DatePickerActions';
import { DatePickerMaskInput } from 'shared/mui/DatePicker/components/DatePickerMaskInput/DatePickerMaskInput';
import {
  DEFAULT_DATE_MASK,
  DEFAULT_DATE_PICKER_MASK,
  DEFAULT_DATE_PICKER_PLACEHOLDER,
} from 'shared/mui/DatePicker/config/defaultValues';
import {
  PopoverCalendarContainerStyled,
  PopoverContentStyled,
  PopoverDatesStyled,
  PopoverLayoutStyled,
} from 'shared/mui/DatePicker/DatePicker.styled';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { formatInputToPlaceholder } from 'shared/mui/DatePicker/utils/formatInputToPlaceholder';
import { getValidCalendarDate } from 'shared/mui/DatePicker/utils/getValidCalendarDate';
import { setCurrentTime } from 'shared/mui/DatePicker/utils/setCurrentTime';
import { formatInputToTimestamp } from 'utils/date';

export type DateRangePickerValue = number | null;

export type DatePickerProps = Omit<TextFieldProps, 'value' | 'onChange' | 'defaultValue'> & {
  minDate?: Date | string | number;
  maxDate?: Date | string | number;
  value: DateRangePickerValue;
  onChange?: (newValue: DateRangePickerValue) => void;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  variant = 'outlined',
  disabled,
  onChange: onChangeProp,
  minDate,
  maxDate,
  value,
  size = 'small',
  ...restProps
}) => {
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const [error, setError] = useState(false);

  const [resetValue, setResetValue] = useState<DateRangePickerValue>(value);

  const [inputValue, setInputValue] = useState<string | undefined>(
    formatInputToPlaceholder(value, { format: DEFAULT_DATE_MASK }),
  );

  const [calendarValue, setCalendarValue] = useState<DateRangePickerValue>(value);

  const open = Boolean(anchorEl);
  const id = open ? 'date-time-picker-popover' : undefined;

  useEffect(() => {
    setInputValue(formatInputToPlaceholder(value, { format: DEFAULT_DATE_MASK }));
    setCalendarValue(value);
  }, [value]);

  const debouncedOnChangeInputHandler = useDebounce((target: string) => {
    const startDate = target;

    if (!startDate) {
      setError(false);
      setCalendarValue(null);
      setInputValue(undefined);
      return;
    }

    setError(true);
    const validStartDate = formatInputToTimestamp(startDate, { format: DEFAULT_DATE_MASK });

    if (validStartDate) {
      const newValue = setCurrentTime(validStartDate, 0);

      setError(false);
      setCalendarValue(newValue);
    } else {
      setCalendarValue(null);
    }

    setInputValue(target);
  }, 300);

  function onChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedOnChangeInputHandler(event?.target?.value);
  }

  function onChangeCalendar(value: DateObject | null) {
    const validStart = getValidCalendarDate(value);

    setInputValue(formatInputToPlaceholder(validStart, { format: DEFAULT_DATE_MASK }));
    setCalendarValue(validStart);
  }

  function onClickApply() {
    if (onChangeProp) {
      onChangeProp(calendarValue);
      setResetValue(resetValue);
      handleClose();
    }
  }

  function onClickReset() {
    if (onChangeProp) {
      onChangeProp(resetValue);
      setInputValue(formatInputToPlaceholder(resetValue, { format: DEFAULT_DATE_MASK }));
      setCalendarValue(resetValue);
      handleClose();
    }
  }

  return (
    <>
      <DatePickerMaskInput
        {...restProps}
        disabled={disabled}
        error={error || restProps?.error}
        label={label}
        mask={DEFAULT_DATE_PICKER_MASK}
        onChange={onChangeInput}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          handleClick(e);
        }}
        placeholder={DEFAULT_DATE_PICKER_PLACEHOLDER}
        size={size}
        value={inputValue}
        variant={variant}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableAutoFocus={true}
        disableScrollLock={true}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <PopoverLayoutStyled
          sx={{
            height: '395px',
          }}
        >
          <PopoverCalendarContainerStyled>
            <PopoverContentStyled>
              <PopoverDatesStyled>
                <PopoverCalendarContainerStyled>
                  <Calendar
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={onChangeCalendar}
                    value={calendarValue}
                  />
                </PopoverCalendarContainerStyled>
              </PopoverDatesStyled>
            </PopoverContentStyled>
          </PopoverCalendarContainerStyled>
          <DatePickerActions sx={{ marginTop: 'auto' }}>
            <Button onClick={onClickReset} variant="outlined">
              Отменить
            </Button>
            <Button onClick={onClickApply} variant="contained">
              Подтвердить
            </Button>
          </DatePickerActions>
        </PopoverLayoutStyled>
      </Popover>
    </>
  );
};
